import _ from 'lodash'
import { LayoutDataItems, TypeMapping, ComplexVariant } from '@wix/thunderbolt-becky-types'
import type {
	ExtractedLayoutWithBreakpointRange,
	IntersectionsWithRanges,
	LayoutWithBreakpointDataAndVariants,
	LayoutWithFullRange,
	Range,
} from '../../types/responsive'
import {
	addOpposingRangesByTruthTable,
	filterByLayoutTypeAndBreakpointId,
	getIntersection,
	getTruthTable,
	layoutsToCloseRanges,
	omitLayoutKeys,
	toOpenRanges,
} from './toNonCoveredRangesUtils'

const getIntersectionsByBreakpointRange = <T extends LayoutDataItems>(
	rangeAndVariantsObj: LayoutWithFullRange<T>,
	visitedRanges: Array<LayoutWithFullRange<T>>
) => {
	const intersectionsByBreakpointRange: Array<IntersectionsWithRanges> = []
	const truthTable = getTruthTable(visitedRanges.length)

	const visitedRangesWithOpposingRanges = addOpposingRangesByTruthTable(truthTable, visitedRanges)

	visitedRangesWithOpposingRanges.forEach((currOption, i) => {
		const intersection = currOption.reduce(
			(accumilativeRanges, rangesArray) => {
				const accRanges: Array<Range> = []
				rangesArray.forEach((currRange) => {
					accumilativeRanges.forEach((currAccRange) => {
						const currIntersection = getIntersection(currAccRange, currRange)
						if (!(currIntersection.min > currIntersection.max || _.isEmpty(currRange))) {
							accRanges.push(currIntersection)
						}
					})
				})

				return accRanges
			},
			[rangeAndVariantsObj.breakpointRange]
		)

		if (!_.isEmpty(intersection)) {
			const intersectedIds: Array<number> = []
			for (let j = 0; j < visitedRanges.length; j++) {
				if (truthTable[i][j]) {
					intersectedIds.push(visitedRanges[j].visitedId)
				}
			}

			intersectionsByBreakpointRange.push({ ranges: intersection, intersectedIds })
		}
	})

	return intersectionsByBreakpointRange
}

const sortByVariantsContainment = <T extends LayoutDataItems>(
	layoutWithVariantsA: LayoutWithBreakpointDataAndVariants<T>,
	layoutWithVariantsB: LayoutWithBreakpointDataAndVariants<T>,
	breakpointOrder: Array<string>
) => {
	const variantsA = layoutWithVariantsA.variants || []
	const variantsB = layoutWithVariantsB.variants || []

	const shouldSwapBPs =
		layoutWithVariantsA.breakpointId && layoutWithVariantsB.breakpointId
			? breakpointOrder.indexOf(layoutWithVariantsA.breakpointId) <
			  breakpointOrder.indexOf(layoutWithVariantsB.breakpointId)
				? 1
				: -1
			: 1 // If we don't have breakpoint Ids we don't want to swap

	const variantAInnerIds = variantsA.map((variant) => variant.id)
	const variantBInnerIds = variantsB.map((variant) => variant.id)

	const isAContainingB = variantsA.every((variant) => variantBInnerIds.includes(variant.id)) ? 1 : 0
	const isBContainingA = variantsB.every((variant) => variantAInnerIds.includes(variant.id)) ? -1 : 0
	const shouldSwapVariants = (isAContainingB === 0 && isBContainingA === 0) || isAContainingB + isBContainingA < 0

	// We are actually swapping when there is NO containment because we want to run in reverse
	// So shouldSwapVariants=true represents NO containment
	return shouldSwapVariants ? -1 : shouldSwapBPs
}

export const extractLayoutToNonCoveredRanges = <T extends LayoutDataItems['type'], K extends keyof TypeMapping[T]>(
	layoutType: T,
	layoutPropKey: K,
	predicate: (v: TypeMapping[T][K]) => boolean,
	relatedLayoutPropKeys: Array<K> = []
) => (
	rootCompLayouts: Array<LayoutWithBreakpointDataAndVariants<LayoutDataItems>>,
	breakpointsOrder: Array<string>
) => {
	let filteredLayouts = layoutsToCloseRanges(
		filterByLayoutTypeAndBreakpointId(rootCompLayouts, layoutType, layoutPropKey)
	)

	if (filteredLayouts.length > 0) {
		// Find the default item
		const defaultLayoutIndex = filteredLayouts.findIndex(
			(layout) => !layout.breakpointId && layout.variants.length === 0
		)
		let defaultLayout: typeof filteredLayouts = []
		if (defaultLayoutIndex !== -1) {
			defaultLayout = filteredLayouts.splice(defaultLayoutIndex, 1)
		}

		filteredLayouts = filteredLayouts
			.sort((a, b) => sortByVariantsContainment(a, b, breakpointsOrder))
			.concat(defaultLayout)
	}

	let visitedLayouts: Array<LayoutWithFullRange<TypeMapping[T]>> = []
	const newLayouts: Array<ExtractedLayoutWithBreakpointRange<TypeMapping[T]>> = []

	filteredLayouts.forEach(({ variants, breakpointRange, layout }, i) => {
		const visitedLayoutWithId = {
			variants,
			breakpointRange,
			layout,
			visitedId: i,
		} as LayoutWithFullRange<TypeMapping[T]>

		if (predicate((layout as TypeMapping[T])[layoutPropKey])) {
			visitedLayouts = visitedLayouts.filter((visitedLayout) =>
				_.some(
					[layoutPropKey, ...relatedLayoutPropKeys],
					(prop) => !_.isEqual(visitedLayout.layout[prop], layout[prop])
				)
			)

			const newLayoutObject = {
				type: layoutType,
				[layoutPropKey]: layout[layoutPropKey],
			}

			_.forEach(relatedLayoutPropKeys, (relatedLayoutPropKey) => {
				if (layout[relatedLayoutPropKey]) {
					newLayoutObject[`${relatedLayoutPropKey}`] = layout[relatedLayoutPropKey]
				}
			})

			if (_.isEmpty(visitedLayouts)) {
				newLayouts.push({
					layout: (newLayoutObject as unknown) as Partial<TypeMapping[T]>,
					breakpointRange,
					...(variants ? { variants } : {}),
				})
			} else {
				const intersectionsByRange = getIntersectionsByBreakpointRange(
					visitedLayoutWithId,
					visitedLayouts.reverse()
				)

				intersectionsByRange.forEach((intersection) => {
					const newComplexVariants: Array<ComplexVariant> = []
					let shouldAddNewLayoutObject = true
					intersection.intersectedIds.forEach((id: number) => {
						const layoutWithVariantsToTurnToNot = visitedLayouts.find(
							(currLayout) => currLayout.visitedId === id
						)! // We definitely have this id in layoutsWithoutCascadingProperty because
						// intersectedIds was constructed using it

						const variantsToTurnToNot = layoutWithVariantsToTurnToNot.variants

						// If we don't have variants on the object we're supposed to NOT on ->
						// it means we shouldn't add the object at all
						if (_.isEmpty(variantsToTurnToNot)) {
							shouldAddNewLayoutObject = false
						} else {
							const lastVariant = _.last(variantsToTurnToNot)
							const complexVariant = {
								type: 'Complex',
								isNot: true,
								variants: variantsToTurnToNot,
								componentId:
									lastVariant?.type !== 'Mobile' && lastVariant?.type !== 'Language'
										? lastVariant?.componentId
										: '',
							} as ComplexVariant

							newComplexVariants.push(complexVariant)
						}
					})

					if (shouldAddNewLayoutObject) {
						intersection.ranges.forEach((bpRange: Range) => {
							newLayouts.push({
								layout: (newLayoutObject as unknown) as Partial<TypeMapping[T]>,
								breakpointRange: bpRange,
								variants: [...(variants || []), ...newComplexVariants],
							})
						})
					}
				})
			}
		}

		visitedLayouts.push(visitedLayoutWithId)
	})

	return rootCompLayouts
		.map((rootLayout) =>
			rootLayout.layout.type === layoutType && predicate((rootLayout.layout as TypeMapping[T])[layoutPropKey])
				? omitLayoutKeys(
						rootLayout as ExtractedLayoutWithBreakpointRange<TypeMapping[T]>,
						_.concat(relatedLayoutPropKeys, [layoutPropKey])
				  )
				: rootLayout
		)
		.concat(toOpenRanges(newLayouts)) as Array<LayoutWithBreakpointDataAndVariants<LayoutDataItems>>
}
